import { useEffect, useState, type ReactNode } from 'react';
import {
  MapPin,
  Map,
  Info,
  CheckCircle,
  AlertCircle,
  Database,
  FolderTree,
  Loader2,
} from 'lucide-react';
import Tooltip from './Tooltip';
import { apiFetch } from '../lib/apiFetch';
import type {
  StoredGeographicalAddress,
  StoredGeographicalStreet,
  StoredRatingValuation,
  StoredParcel,
} from '../types/geographicalContext';

const PNCC_GIS_BASE =
  'https://maps.pncc.govt.nz/portal/apps/mapviewer/index.html?find=';

export function openPnccGisFind(query: string): void {
  window.open(`${PNCC_GIS_BASE}${encodeURIComponent(query)}`, '_blank', 'noopener,noreferrer');
}

export function ValuationMapImage({
  valuationReferenceNumber,
  alt,
}: {
  valuationReferenceNumber: string;
  alt: string;
}) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;

    setBlobUrl(null);
    setLoaded(false);
    setHasError(false);

    apiFetch(
      `/api/v1/rating_valuations/${encodeURIComponent(valuationReferenceNumber)}/image`,
    )
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`Image fetch failed: ${response.status}`);
        }
        return response.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        if (cancelled) {
          URL.revokeObjectURL(objectUrl);
          objectUrl = null;
          return;
        }
        setBlobUrl(objectUrl);
      })
      .catch(() => {
        if (!cancelled) {
          setHasError(true);
        }
      });

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [valuationReferenceNumber]);

  if (hasError) {
    return null;
  }

  return (
    <div className="relative mt-2 rounded-md overflow-hidden border border-amber-200 hover:border-amber-400 transition-colors">
      {(!blobUrl || !loaded) && (
        <div className="flex items-center justify-center bg-amber-50 w-full h-24">
          <Loader2 className="w-5 h-5 text-amber-400 animate-spin" />
        </div>
      )}
      {blobUrl && (
        <img
          src={blobUrl}
          alt={alt}
          onLoad={() => setLoaded(true)}
          className={`w-full object-cover transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0 absolute inset-0'}`}
        />
      )}
    </div>
  );
}

export function GeographicalContextCard({
  tooltipContent,
  children,
}: {
  tooltipContent: string;
  children: ReactNode;
}) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
      <div className="p-4 bg-gray-50/50 border-b border-gray-100 flex items-center gap-2 rounded-t-xl">
        <MapPin className="w-4 h-4 text-emerald-600" />
        <h3 className="text-sm font-bold text-gray-900 flex items-center">
          Geographical Context
          <Tooltip content={tooltipContent}>
            <Info className="w-3.5 h-3.5 text-gray-400 ml-1.5 cursor-help" />
          </Tooltip>
        </h3>
      </div>
      {children}
    </div>
  );
}

export function GeographicalContextGisButton({
  query,
  disabled,
}: {
  query: string;
  disabled?: boolean;
}) {
  const isDisabled = disabled || !query;
  return (
    <button
      type="button"
      disabled={isDisabled}
      onClick={() => {
        if (query) openPnccGisFind(query);
      }}
      className="w-full py-2 bg-gray-50 hover:bg-emerald-50 text-gray-600 hover:text-emerald-700 text-xs font-bold rounded-lg border border-gray-200 hover:border-emerald-200 transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <Map className="w-3.5 h-3.5 mr-2" />
      View in GIS
    </button>
  );
}

/** Read-only addresses + street for persisted record shape. */
export function GeographicalContextAddressesReadOnly({
  street,
  addresses,
}: {
  street?: StoredGeographicalStreet | null;
  addresses?: StoredGeographicalAddress[];
}) {
  const hasStreet = !!street?.formatted_street;
  const addrList = addresses ?? [];
  if (!hasStreet && addrList.length === 0) return null;

  return (
    <div className="border-t border-gray-100 pt-3">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Addresses</p>
      {hasStreet && (
        <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 rounded-lg text-sm text-blue-800">
          <Map className="w-3.5 h-3.5 text-blue-500 shrink-0" />
          <span>{street!.formatted_street}</span>
          <Tooltip content="Street validated against GIS">
              <span className="ml-auto shrink-0">
                <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
              </span>
            </Tooltip>
        </div>
      )}
      {addrList.map((addr, i) => (
        <div
          key={i}
          className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg text-sm text-gray-700 mt-1"
        >
          <MapPin className="w-3.5 h-3.5 text-gray-400 shrink-0" />
          <span>{addr.formatted_address}</span>
          <Tooltip content="Address validated against GIS">
            <span className="ml-auto shrink-0">
              <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
            </span>
          </Tooltip>
        </div>
      ))}
    </div>
  );
}

export interface ProcessingParsedAddress {
  formatted_address?: string;
  street_number?: number;
  street_name?: string;
  street_type?: string;
  suburb?: string;
  town_or_city?: string;
  latitude?: number;
  longitude?: number;
}

export interface ProcessingParsedStreet {
  formatted_street?: string;
  street_name?: string;
  street_type?: string;
  town_or_city?: string;
}

/** Parsed + validated address rows (ManualUpload processing). */
export function GeographicalContextAddressesProcessing({
  parsed_street,
  validated_street,
  parsed_addresses,
  validated_addresses,
}: {
  parsed_street?: ProcessingParsedStreet | null;
  validated_street?: { formatted_street?: string } | null;
  parsed_addresses?: ProcessingParsedAddress[];
  validated_addresses?: Array<{ formatted_address?: string; latitude?: number; longitude?: number }>;
}) {
  const hasParsed = (parsed_addresses?.length ?? 0) > 0 || !!parsed_street;
  if (!hasParsed) return null;

  return (
    <div className="border-t border-gray-100 pt-3">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Addresses</p>
      {parsed_street && (
        <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 rounded-lg text-sm text-blue-800">
          <Map className="w-3.5 h-3.5 text-blue-500 shrink-0" />
          <span>{parsed_street.formatted_street}</span>
          {validated_street ? (
            <Tooltip content="Street validated against GIS">
              <span className="ml-auto shrink-0">
                <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
              </span>
            </Tooltip>
          ) : (
            <Tooltip content="Unable to validate against GIS">
              <span className="ml-auto shrink-0">
                <AlertCircle className="w-3.5 h-3.5 text-amber-400" />
              </span>
            </Tooltip>
          )}
        </div>
      )}
      {parsed_addresses?.map((addr, i) => {
        const isValidated = validated_addresses?.some((v) => v.formatted_address === addr.formatted_address);
        return (
          <div
            key={i}
            className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg text-sm text-gray-700 mt-1"
          >
            <MapPin className="w-3.5 h-3.5 text-gray-400 shrink-0" />
            <span>{addr.formatted_address}</span>
            {isValidated ? (
              <Tooltip content="Address validated against GIS">
                <span className="ml-auto shrink-0">
                  <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
                </span>
              </Tooltip>
            ) : (
              <Tooltip content="Address not validated against GIS">
                <span className="ml-auto shrink-0">
                  <AlertCircle className="w-3.5 h-3.5 text-amber-400" />
                </span>
              </Tooltip>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function GeographicalContextValuationsParcels({
  rating_valuations,
  parcels,
}: {
  rating_valuations?: StoredRatingValuation[];
  parcels?: StoredParcel[];
}) {
  const hasRv = (rating_valuations?.length ?? 0) > 0;
  const hasParcels = (parcels?.length ?? 0) > 0;
  if (!hasRv && !hasParcels) return null;

  return (
    <div className="border-t border-gray-100 pt-3">
      {hasRv ? (
        <div className="mb-2">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Rating Valuations</p>
          {rating_valuations!.map((val, i) => (
            <div key={i} className="px-3 py-2 bg-amber-50 rounded-lg text-sm text-amber-900 mt-1">
              <div className="flex items-center gap-2">
                <Database className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                <span className="font-medium">{val.valuation_reference_number}</span>
              </div>
              {val.valuation_reference_number ? (
                val.image_url ? (
                  <a href={val.image_url} target="_blank" rel="noopener noreferrer" className="block cursor-pointer">
                    <ValuationMapImage
                      valuationReferenceNumber={val.valuation_reference_number}
                      alt={`Map for ${val.valuation_reference_number}`}
                    />
                  </a>
                ) : (
                  <ValuationMapImage
                    valuationReferenceNumber={val.valuation_reference_number}
                    alt={`Map for ${val.valuation_reference_number}`}
                  />
                )
              ) : null}
            </div>
          ))}
        </div>
      ) : null}
      {hasParcels ? (
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Parcels</p>
          {parcels!.map((parcel, i) => (
            <div
              key={i}
              className="flex items-center gap-2 px-3 py-2 bg-violet-50 rounded-lg text-sm text-violet-800 mt-1"
            >
              <FolderTree className="w-3.5 h-3.5 text-violet-500 shrink-0" />
              <span>{parcel.parcel_id}</span>
              <span className="text-xs text-violet-600">{parcel.appellation}</span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Editable geographical context card — shared layout for ManualUpload
 * and RecordDetails edit mode. Renders the address input, GIS button,
 * and delegates address/valuation display to children.
 */
export function GeographicalContextEditable({
  tooltipContent,
  suggestedAddress,
  onAddressChange,
  onValidate,
  isValidating,
  readOnly,
  showValidateButton,
  gisQuery,
  gisDisabled,
  children,
}: {
  tooltipContent: string;
  suggestedAddress: string;
  onAddressChange: (value: string) => void;
  onValidate: () => void;
  isValidating: boolean;
  readOnly?: boolean;
  showValidateButton?: boolean;
  gisQuery?: string;
  gisDisabled?: boolean;
  children?: ReactNode;
}) {
  const effectiveGisQuery = gisQuery ?? suggestedAddress;
  const effectiveGisDisabled = gisDisabled ?? !effectiveGisQuery;

  return (
    <GeographicalContextCard tooltipContent={tooltipContent}>
      <div className="p-4 space-y-3">
        <div className="relative">
          {isValidating
            ? <Loader2 className="absolute left-3 top-3 w-4 h-4 text-emerald-500 animate-spin" />
            : <MapPin className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
          }
          <input
            type="text"
            className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all outline-none"
            placeholder="No address detected... (press Enter to validate)"
            value={suggestedAddress}
            onChange={(e) => onAddressChange(e.target.value)}
            readOnly={readOnly}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !readOnly) onValidate();
            }}
          />
        </div>
        {showValidateButton && (
          <button
            type="button"
            onClick={onValidate}
            disabled={isValidating}
            className="w-full py-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-bold rounded-lg border border-emerald-200 hover:border-emerald-300 transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isValidating ? (
              <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />
            ) : (
              <CheckCircle className="w-3.5 h-3.5 mr-2" />
            )}
            {isValidating ? 'Validating...' : 'Validate Address'}
          </button>
        )}
        <GeographicalContextGisButton
          query={effectiveGisQuery}
          disabled={effectiveGisDisabled}
        />
        {children}
      </div>
    </GeographicalContextCard>
  );
}
